import { gql } from "@apollo/client";
import { useMemo } from "react";

// Components
import Head from "next/head";
import Link from "next/link";
import Header from "@/components-layout/header/header";
import Footer from "@/components-layout/footer/footer";
import Container from "@/components-layout/container/container";
// import IconFA from "@/components/icon-fa/icon-fa";
import IngredientForm from "@/components/ingredientForm/ingredient-form";
import ScrollBtn from "@/components/scroll-btn/scroll-btn";

// Constants
import { genSetVars } from "@/constants/gql-fields";

// Style
import style from "@/styles/archive-ingredient.module.scss";

const alph = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

function setIngrdDict(ingrds) {
  const ingrdDict = ingrds.reduce((a, ingrd) => {
    const k = ingrd.title[0].toUpperCase();
    const altPos = ingrd.ingredientsFields?.customIndexPosition?.toUpperCase();
    const altNames = ingrd.ingredientsFields.alternateIndexNames;

    if (altPos && a[altPos]) {
      a[altPos].push(ingrd);
      return a;
    } else if (altPos) {
      a[altPos] = [ingrd];
      return a;
    }

    if (altNames) {
      altNames.map((alt, i) => {
        const char = alt.ingredientName[0].toUpperCase();
        const altIngrd = {
          ...ingrd,
          title: alt.ingredientName,
          id: ingrd.id + "_" + i,
        };

        if (a[char]) {
          a[char].push(altIngrd);
          return a;
        } else {
          a[char] = [altIngrd];
          return a;
        }
      });
    }

    if (a[k]) {
      // either push to an existing dict entry or create one
      a[k].push(ingrd);
    } else {
      a[k] = [ingrd];
    }

    return a;
  }, {});
  return ingrdDict;
}

const ListIngrd = (props) => {
  return (
    <li className={style.ingrd}>
      <Link href={props.uri}>
        {props.title} →{/* <IconFA name="faArrowRight" /> */}
      </Link>
    </li>
  );
};

export default function Component(props) {
  // Loading state for previews
  if (props.loading) {
    return <>Loading...</>;
  }

  // Variables
  const data = props.data;
  // const siteTitle = data.generalSettings.title;
  const ingredients = data.ingredients.nodes;
  const ingrFormData = data.page?.pageIngredientForm;
  const title = "Ingredients";

  const dict = useMemo(() => setIngrdDict(ingredients), [ingredients]);

  return (
    <>
      <Head>
        {/* <title>{`${title} - ${siteTitle}`}</title> */}
        <title>{data.page.seo.title}</title>
        <meta name="description" content={data.page.seo.metaDesc} />
      </Head>

      <Header entry={data.generalSettingsOptionsPage.generalSettingsACF} />

      <main className="archiveIngredients">
        <section className={style.secHeader}>
          <Container>
            <h1 className={style.title}>Ingredient Index</h1>
          </Container>

          <div className={style.jumpLinksWrapper}>
            <div className={style.jumpLinks}>
              {/* {Object.entries(dict).map((entry) => {
                  const letter = entry[0];
                  return (
                    <Link href={"#ingrd-" + letter.toLowerCase()} key={letter}>
                      {letter}
                    </Link>
                  );
                })} */}

              {alph.map((letter) => {
                if (dict[letter]) {
                  return (
                    <Link href={"#ingrd-" + letter.toLowerCase()} key={letter}>
                      {letter}
                    </Link>
                  );
                } else {
                  return <p key={letter}>{letter}</p>;
                }
              })}
            </div>
          </div>
        </section>

        <section className={style.secIngrds}>
          <Container>
            {alph.map((letter) => {
              const key = letter;
              if (dict[letter]) {
                return (
                  <div className={style.indexSection} key={key}>
                    <h2 className={style.indexTitle}>{key}</h2>
                    <ul
                      className={style.ingrdList}
                      id={"ingrd-" + key.toLowerCase()}
                    >
                      {dict[letter]
                        .sort((a, b) =>
                          a.title.localeCompare(b.title, undefined, {
                            ignorePunctuation: true,
                            numeric: true,
                          })
                        )
                        .map((ingrd) => {
                          return <ListIngrd {...ingrd} key={ingrd.id} />;
                        })}
                    </ul>
                  </div>
                );
              } else {
                return (
                  <div className={style.indexSectionEmpty} key={key}>
                    <h2 className={style.indexTitle}>{key}</h2>
                  </div>
                );
              }
            })}

            {/* Only show index blocks for items with results */}
            {/* {Object.entries(dict).map((entry, i) => {
              const key = entry[0];
              const value = entry[1];

              return (
                <div className={style.indexSection} key={key}>
                  <h2 className={style.indexTitle}>{key}</h2>
                  <ul
                    className={style.ingrdList}
                    id={"ingrd-" + key.toLowerCase()}
                  >
                    {value.map((ingrd) => {
                      return <ListIngrd {...ingrd} key={ingrd.id} />;
                    })}
                  </ul>
                </div>
              );
            })} */}
          </Container>
        </section>

        {ingrFormData && <IngredientForm data={ingrFormData} />}

        <ScrollBtn />
      </main>

      <Footer entry={data.generalSettingsOptionsPage.generalSettingsACF} />
    </>
  );
}

Component.variables = (seedQuery, ctx) => {
  return {
    uri: seedQuery.uri,
  };
};

Component.query = gql`
  query GetArchive($uri: ID!) {
    # -- Site Settings
    # generalSettings {
    #   title
    # }

    page(id: 972, idType: DATABASE_ID) {
      pageIngredientForm {
        id
        __typename
        ...IngredientFormFragment
      }
      seo {
        metaDesc
        # fullHead
        title
      }
    }

    # -- Ingredients Data
    ingredients(first:500 where: {orderby: {field: TITLE, order: ASC}, status: PUBLISH}) {
      ### --- Increase max posts in functions.php --- ###
      __typename
      nodes {
        uri
        id
        title
        ingredientsFields {
          alternateIndexNames {
            __typename
            ingredientName
          }
          customIndexPosition
        }
      }
    }

    # Gravity Forms
    ...GFormFragment

    # -- Header
    ...HeaderFragment @unmask(mode: "migrate")

    # -- Footer Fragment
    ...FooterFragment @unmask(mode: "migrate")

    # -- General Settings Variables
    ${genSetVars}
  }
  ${Header.fragments.entry}
  ${Footer.fragments.entry}
  ${IngredientForm.fragments.gforms}
  ${IngredientForm.fragments.copy}
`;
